/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * The one `MappingLeaf` ⇄ row round trip (#1128).
 *
 * **Sharing the widget is not sufficient — share the round trip.** Every mapping surface
 * already used `MappingControl`, and each then wrote its *own* projection from the shared
 * {@link MappingLeaf} into its own row shape. Round-tripping one authored mapping — a path
 * with a default and a description — through the three surfaces showed parameters and
 * headers dropping both while the ORTB body kept them, because the two that lose are
 * near-verbatim copies of each other (`hasRegexOrTransform`, `macroTokenName` and the
 * collapse were duplicated line for line).
 *
 * So: **a surface may differ in what it maps to; it may not differ in what a mapping is.**
 * What a mapping *is* lives here. What a row can *hold* is declared by the caller through
 * {@link RowValueSlots}, because that genuinely differs — a parameter row has its own
 * description field and a header row does not.
 */

import { declaresDefaultSlot } from '@expression-default-rule';
import type { ExpressionNameLookup } from './expression-summary';
import { referenceIsBehind } from './shared-expression-version';
import { canonicalizeContextPath } from '@context-roots';
import type { OmissionAnswer } from '@wire-omission';
import { artifactFieldAt } from '../inputs/data-model-schema';
import type {
  CalculatedExpression,
  ComputedExpression,
  CriteriaNode,
  MacroExpression,
  MacroTokenExpression,
} from '../../../types/decorator-expression';
import type { MappingLeaf } from './ortb-body-editor.adapters';
import { formatStaticValue } from './ortb-static-value';

export type RowMappingKind = 'static' | 'macro' | 'path' | 'expression' | 'empty';

/**
 * The value half of a row — **one slot per answer** since `N11f` (`#1177`, 2026-08-26).
 *
 * `value` used to be the authored literal *or* the `{macro}` token, which is why the macro
 * tab had nowhere to put a default: the macro and its default were the same string, so a
 * default on a macro row was unrepresentable rather than unbuilt. The split has a driver
 * half — `libs/dto/src/mapping-value-slots.ts` is the one reader of a document written
 * before it — and this is the console's side of the same three slots.
 */
export interface RowValueSlots {
  /** The authored literal, and nothing else. */
  value: string;
  /**
   * **Everything that is not a literal** (`X58`, `#1428`): a path, a `{macro}` (a `macro` node), a
   * calculation, a decision. It carries its own default, so a row has no second slot for one.
   */
  expression?: MacroExpression;
  /**
   * Only meaningful when the row {@link RowValueCapabilities.absorbsDescription absorbs}
   * one; `undefined` means *leave whatever the row already had*.
   */
  description?: string;
}

export interface RowValueCapabilities {
  /**
   * The row has a description field of its own, so a plain path's description collapses
   * into it rather than holding the expression open.
   *
   * **This is not a preference — it decides what goes on the wire.** A parameter resolved
   * through `expression` always yields a value (the engine returns its default, `''` when
   * there is none), while one resolved through `mappingSource` yields `undefined` and is
   * omitted. So holding a *description-only* expression open on a parameter row would turn
   * an omitted optional parameter into `foo=`. A header has no such asymmetry — its
   * `mappingSource` is converted to a `calculated` expression before it is resolved, and an
   * empty result is omitted either way (#998) — so it keeps the expression instead.
   */
  absorbsDescription?: boolean;
}

/**
 * **The one place an authored expression's default is read, console-side** (`X3`, `#1216`).
 *
 * `default` is the word; `fallback` is what it was called until 2026-09-02 and is what a stored
 * expression still carries until it is next saved. The driver reads both through
 * `declaredDefault` in `macro-expression.engine.ts`; this is the mirror, and it exists for the
 * reason the mirror always does — the console is a separate Vite app that cannot import it.
 */
export function expressionDefault<T extends string | number>(node: { default?: T; fallback?: T }): T | undefined {
  return node.default !== undefined ? node.default : node.fallback;
}

/**
 * Write a node's default, dropping the legacy spelling.
 *
 * A save writes `default`; leaving `fallback` beside it would put two answers on one expression
 * and the next reader would have to decide between them — which is the defect `#1216` closes.
 */
export function withExpressionDefault<T extends { default?: string | number; fallback?: string | number }>(
  node: T,
  defaultValue: string | undefined,
): T {
  const { fallback: _legacy, ...rest } = node;
  return { ...rest, default: defaultValue } as T;
}

/** The macro name inside a `{token}` / `[token]`, or null. */
export function macroTokenName(value: string): string | null {
  const m = /^[[{]\{?\s*([A-Za-z0-9_]+)\s*\}?[\]}]$/.exec(value.trim());
  return m ? m[1] : null;
}

/**
 * **An expression that is only a path** — `calculated` with no Extract and no Transform. Such a row
 * hands its description to the row's own note (`absorbsDescription`), which is the one place a path
 * and the row it sits in were ever told apart. It is stored as an expression like everything else
 * (`X58`); nothing collapses to `mappingSource` any more.
 */
export function isPlainPath(expr: MacroExpression | undefined): expr is CalculatedExpression {
  return !!expr && expr.type === 'calculated' && !expr.regex && !expr.transform;
}

/**
 * **The row's default** — on its expression, the one place a row keeps one (`X58`). An alias of
 * {@link expressionDefaultOf} with no library to consult, for a caller holding only the slots.
 */
export function slotsDefault(slots: Pick<RowValueSlots, 'expression'>): string | undefined {
  return expressionDefaultOf(slots.expression, null);
}

/** Project a row's value slots into the `MappingLeaf` the shared control edits. */
export function leafFromRowSlots(path: MappingLeaf['path'], slots: RowValueSlots): MappingLeaf {
  if (slots.expression) {
    // A description the row absorbed from a plain path is handed back, so the operator sees what they
    // wrote wherever they open the mapping.
    const expression =
      isPlainPath(slots.expression) && !slots.expression.description && slots.description
        ? { ...slots.expression, description: slots.description }
        : slots.expression;
    return { path, kind: 'expression', expression };
  }
  return { path, kind: 'static', staticValue: slots.value ?? '' };
}

/** Fold an edited `MappingLeaf` back into the row's value slots. */
export function rowSlotsFromLeaf(leaf: MappingLeaf, caps: RowValueCapabilities = {}): RowValueSlots {
  if (leaf.kind === 'static' || !leaf.expression) {
    // A static value is its own answer, so it has no default to carry — CLAUDE.md says so
    // (*where it does not apply*).
    return { value: leaf.kind === 'static' ? formatStaticValue(leaf.staticValue) : '', expression: undefined };
  }
  const expr = leaf.expression;
  if (caps.absorbsDescription && isPlainPath(expr)) {
    const { description, ...rest } = expr;
    return { value: '', expression: rest, description: description ?? '' };
  }
  return { value: '', expression: expr };
}

/** A one-line summary of a row's current mapping — for the collapsed row chip. */
/**
 * **The pill for a row whose value is an expression — one rule for every mapping surface.**
 *
 * A path reads as a path, whether or not it carries a default, a regex or a transform: the chip names
 * where the value comes from, and the summary still says `/regex/` where one applies. The list editors
 * used to turn a path with a regex or transform into an `expression` chip while the ORTB body kept it a
 * path, so one mapping wore two colours on one page (2026-09-15, Todd: *"they should match — and I like
 * the more detail (path)"*). Every other shape reads through `summarize`, which names a shared
 * expression where the surface can.
 */
export function describeExpressionPill(
  expression: MacroExpression,
  summarize: (expr: MacroExpression) => string,
): { label: string; kind: RowMappingKind } {
  // A macro still reads as the macro the operator typed (`X27`); the path it reaches is the value
  // line's to state.
  if (expression.type === 'macro') return { label: `{${expression.name}}`, kind: 'macro' };
  return { label: summarize(expression), kind: expression.type === 'calculated' ? 'path' : 'expression' };
}

export function describeRowSlots(
  slots: RowValueSlots,
  summarize: (expr: MacroExpression) => string,
): { label: string; kind: RowMappingKind } {
  if (slots.expression) return describeExpressionPill(slots.expression, summarize);
  const value = (slots.value ?? '').trim();
  if (!value) return { label: 'not set', kind: 'empty' };
  return { label: value, kind: 'static' };
}

/**
 * **Does this row say anything at all** — one slot filled, whichever it is.
 *
 * Written out twice before the required mark needed it on a second surface, and it is exactly
 * `N11f`'s subject: *which slot holds the answer* is the model's question, not each caller's.
 */
export function isAnswered(slots: RowValueSlots): boolean {
  return String(slots.value ?? '').trim() !== '' || !!slots.expression;
}

/**
 * **Which expression shapes declare a default** — one home for a rule that was written in three
 * places and disagreed in one (`#1269`, 2026-09-09).
 *
 * `ExpressionDefaultControl`'s docblock already states it — *`calculated || computed` declare one; a
 * **conditional** does not, and correctly, since both branches always produce something or one of
 * them suppresses* — and that component writes it correctly. {@link leafDefault}, the **reader**,
 * stopped at `calculated`, so a `computed` that declared a default had one authored, stored and
 * resolved, and **no surface could see it**.
 *
 * Two operator-facing consequences, and the second is the sharper one:
 *
 * - the row's `⤵` default mark was absent on every calculation, which is `#1269` exactly;
 * - `ortb-body-validation`'s `hasDefault` was false, so a **required** calculation carrying a
 *   default still warned *required and derived with no default* (`X46b`) — **a surface warning
 *   about correct behaviour**, which is how an operator learns to ignore the warnings that matter.
 *
 * That the writer's own docblock states the rule the reader failed to follow is the whole argument
 * for this function existing rather than the condition being written a fourth time.
 */
export function declaresDefault(
  expression: MacroExpression,
): CalculatedExpression | ComputedExpression | MacroTokenExpression | undefined {
  // **The condition is the shared rule, not a copy of it** (`#1269`, raised again by `XS5`). This
  // file and `acceptsReferenceOverride` ask the same question on opposite sides of the mirror, which
  // is exactly the boundary the first fix left in place.
  return declaresDefaultSlot(expression.type)
    ? (expression as CalculatedExpression | ComputedExpression | MacroTokenExpression)
    : undefined;
}

/**
 * **The default authored on a leaf, wherever the leaf keeps it** (`N11f`).
 *
 * A macro leaf keeps its own; an expression leaf keeps it on the expression, because the
 * expression is what the driver resolves and the ORTB body stores it verbatim. One accessor
 * so no surface re-decides which — and *which shapes have one to keep* is
 * {@link declaresDefault}'s answer rather than a second copy of the condition here.
 */
export function leafDefault(leaf: MappingLeaf, lookup: ExpressionNameLookup | null): string | undefined {
  return expressionDefaultOf(leaf.expression, lookup);
}

/**
 * **The default an expression answers with when it resolves to nothing** — {@link leafDefault}'s
 * expression half, for a surface whose rows are expressions rather than ORTB leaves (the response
 * mapping, `4b`). One reader, so the two surfaces cannot disagree about where a default lives.
 */
export function expressionDefaultOf(
  expression: MacroExpression | undefined,
  lookup: ExpressionNameLookup | null,
): string | undefined {
  if (!expression) return undefined;

  /**
   * **A row that addresses a shared expression has a default in two layers** (`X36`, `XS5`), and
   * this read knew about neither until Todd's rehearsal: *"default from mapping shows in pill, not
   * the default treatment."* `declaresDefault` answers *does this shape declare a slot*, and a
   * `reference` does not — its `default` is an **override** of the expression's. So a row carrying
   * one reported no default at all, the `⤵` mark never rendered, and the value surfaced in the
   * summary instead. That is `#1269`'s shape a third time, in the accessor `#1269` was about.
   *
   * The row's own override wins, and the expression's own stands where there is none — which is
   * exactly what the resolver does, so the mark and the wire cannot disagree. **Without a lookup
   * only the override is knowable**, and reporting the inherited value as absent would be a claim
   * this cannot support; the mark is then simply not rendered, which is *say you do not know*.
   *
   * **The lookup is required and nullable rather than optional** (`#1254`). Every one of the six
   * production callers already passed it, so this produced no audit of its own — it is here because
   * the accessor's failure mode when it is missing is *nothing renders*, which nobody reports, and
   * `#1269` is the instance that cost. A surface with no library says `null` and says it out loud.
   */
  if (expression.type === 'reference') {
    if (expression.default !== undefined) return expression.default;
    const target = lookup?.(expression.ref)?.expression;
    const declares = target && declaresDefault(target);
    return declares ? expressionDefault(declares) : undefined;
  }

  const expr = declaresDefault(expression);
  return expr ? expressionDefault(expr) : undefined;
}

/**
 * **The shared expression this leaf addresses has moved since the row recorded it** (`#1302`,
 * ratified by Todd 2026-09-10: *"the row should carry a mark and a notice that the expression
 * changed, the author should revisit"*).
 *
 * The leaf-level accessor beside {@link leafDefault}, and for the same reason: **one derivation, N
 * renderings.** Five editors build a collapsed row's marks and the expanded value line builds its
 * own; six places deciding *has this changed* is six chances for a reader to be narrower than the
 * writer, and a narrow one here produces **no mark**, which looks exactly like nothing having
 * changed (`#1269`).
 *
 * **Without a lookup nothing is claimed**, which is the same rule `leafDefault` follows one function
 * up: the version lives in the library, so a surface that has not been handed it does not know, and
 * *say you do not know* beats a mark built on a request in flight.
 */
export function leafAddressesChangedExpression(leaf: MappingLeaf, lookup?: ExpressionNameLookup): boolean {
  const expression = leaf.kind === 'expression' ? leaf.expression : undefined;
  if (!expression || expression.type !== 'reference') return false;
  return referenceIsBehind(expression, lookup?.(expression.ref)?.version);
}

/** Set a leaf's default, into whichever slot that kind of leaf keeps it in. */
export function withLeafDefault(leaf: MappingLeaf, defaultValue: string | undefined): MappingLeaf {
  const expr = leaf.expression;
  if (!expr || (expr.type !== 'calculated' && expr.type !== 'macro')) return leaf;
  return { ...leaf, expression: withExpressionDefault(expr, defaultValue) };
}

/**
 * What a surface does with a derived value that resolved to nothing and has no default.
 * Named for the operator's outcome rather than for our mechanism.
 *
 * It lives here rather than on `MappingControl` because **two things read it**: the control's
 * hint, and {@link describeRequiredClaim}, which has to say what an unfilled *required* row
 * actually does. Those two sentences describe one behaviour and must not be able to disagree.
 */
export type UnresolvedBehaviour = 'omit' | 'sendEmpty' | 'sendLiteral';

/** The row mark for a required claim — `null` where there is nothing to say. */
export interface RequiredMark {
  readonly label: string;
  readonly tone: 'declaration' | 'operator';
  readonly title: string;
}

/**
 * **The one required-mark projection, for every mapping surface** (`N11g`'s visible half,
 * `#1177`; extended to the ORTB body leaf 2026-08-27).
 *
 * It shipped on the parameter row alone, which is the shape this repo keeps paying for: the claim
 * is a property of a *mapping*, not of a parameter, and a body leaf has carried the same
 * `optional` flag since `#1184` — with the checkbox visible in the expanded editor and the
 * collapsed row saying nothing. *Is this a property of this surface, or of mappings?*
 *
 * Four things decide the wording, each from something already on the row:
 *
 * - **It marks `required`, never `optional`.** Optional is the default and therefore the
 *   majority, and a mark on the majority is noise — which is how a useful mark stops being read.
 * - **It never restates the resolution mark.** `→ omitted` is *observed*; this is *declared*. If
 *   they disagree that is a finding, not a rendering choice.
 * - **Required + unfilled reads by the artifact's kind.** On a template it *is* the shape —
 *   `TemplatesPage`'s **You supply this**, reused rather than phrased again; on an instance it is
 *   unfinished, which validation already warns about, so the badge declares and does not become a
 *   second warning.
 * - **A suppressed row is not marked at all**, and the caller expresses that by reporting
 *   `required: false` — *never send* and *always send* share one `state` slot, so the
 *   contradictory pair is unrenderable.
 *
 * **`whenUnresolved` is why this is a shared projection rather than a shared string.** The claim
 * is identical on both surfaces and the *consequence* is not: a required VAST parameter with
 * nothing in it goes out as `foo=`, while an ORTB body key that resolves to nothing is **omitted**
 * either way (#880). Copying the parameter row's tooltip onto a body leaf would be a true sentence
 * about the wrong surface, which is #1045 inside the mark built to prevent one.
 *
 * @param isTemplate `null` where the surface does not know — a row with no answer then says only
 *   that it is required, which is true on either reading.
 */
export function describeRequiredClaim(
  claim: { required: boolean; answered: boolean; whenUnresolved: UnresolvedBehaviour },
  isTemplate: boolean | null,
): RequiredMark | null {
  if (!claim.required) return null;

  const declaration: RequiredMark = {
    label: 'REQUIRED',
    tone: 'declaration',
    title: REQUIRED_TITLE[claim.whenUnresolved],
  };
  if (claim.answered) return declaration;

  return isTemplate === true
    ? { label: 'You supply this', tone: 'operator', title: 'This template leaves the value to you' }
    : declaration;
}

/**
 * **What each omission answer does on this surface** (`X29`, `X46a`).
 *
 * The select's option labels say the *outcome* and nothing else, which is only half a statement:
 * *always send it* means `foo=` on a query parameter and means nothing at all on a body key, and
 * an operator has no way to know which surface they are on. So the control states what the
 * **selected** answer actually does, which is the resolution-mark rule — *state what is in force,
 * or say you do not know* — applied to a choice rather than to a value.
 *
 * Keyed off {@link UnresolvedBehaviour} because that is already the surface's own spelling of
 * absence, read by the required badge's tooltip ({@link describeRequiredClaim}) and by the
 * required-risk warning ({@link describeRequiredRisk}). Three sentences from one declaration, so
 * the three cannot come to describe one behaviour three ways.
 *
 * **Shortened 2026-09-04 (#1179), and the length was the whole request** — Todd: *"I'm looking for
 * the shortest, clear sentence for the live text."* Roughly halved, with the facts kept: *never*
 * still says it removes the driver's value, and *always* still varies by surface, because those are
 * the two things an operator cannot infer from the option label above them. A sentence that drops
 * the surprising half is shorter without being clearer.
 */
export function describeOmissionAnswer(answer: OmissionAnswer, whenUnresolved: UnresolvedBehaviour): string {
  switch (answer) {
    // **The layering surface supplies this one**, because only it knows what is beneath (`X33`).
    // A generic sentence here would name a mechanism instead of an outcome — see
    // `MappingSurfaceCapabilities.declaresOmission.layering`, which carries the noun.
    case 'inherit':
      return '';
    case 'never':
      // `stripsProceduralValue` is true for this condition, and the second clause is the part an
      // operator does not expect: it is the only answer that also removes what the driver built at
      // that path. **It survives the shortening** — Todd asked for the shortest *clear* sentence,
      // and a sentence that drops the surprising half is shorter without being clear.
      return 'Never sent — and removes whatever the driver built here.';
    case 'whenUnresolved':
      return 'Omitted when nothing resolves.';
    case 'always':
      return ALWAYS_SENT_CONSEQUENCE[whenUnresolved];
  }
}

/**
 * What *always send it* costs when nothing resolves, per surface.
 *
 * The `omit` row is why two of the four surfaces **refuse** the *leave it off* answer: on a body key
 * or a header the two answers are the same request, so offering both would be a choice that decides
 * nothing (pattern 24 — the refusal says so rather than hiding the option).
 */
const ALWAYS_SENT_CONSEQUENCE: Record<UnresolvedBehaviour, string> = {
  sendEmpty: 'Always sent — empty when nothing resolves.',
  omit: 'Sent when it resolves; this surface omits it either way.',
  sendLiteral: 'Always sent — the unresolved token when nothing resolves.',
};

/** The select's option labels — the outcome, never the mechanism. */
export const OMISSION_ANSWER_LABEL: Record<OmissionAnswer, string> = {
  always: 'Always send it',
  whenUnresolved: 'Leave it off if nothing resolves',
  never: 'Never send it',
  /**
   * **A placeholder the control never renders** (`X33`, `XS3` slice 5).
   *
   * *Fall back to…* has to name what it falls back to, and only the layering surface knows —
   * *the source's own value* on a round override, and something else on the next such surface. So
   * the option's label comes from `declaresOmission.layering.beneath`, and a surface that declares
   * no layering never offers the option at all. This entry exists because the record is total over
   * {@link OmissionAnswer}; if it ever reaches a screen, a surface has declared layering without
   * saying what is underneath.
   */
  inherit: 'Fall back to the value beneath',
};

/**
 * **A required field that is not guaranteed to arrive** (`X46`, requested by Todd 2026-09-02):
 * *"a field that's marked required and does not have a value guaranteed to resolve (like no
 * default), or has 'omit if unresolved' should produce a warning in the editor."*
 *
 * This is the finding the 2026-08-27 ruling on the body's required badge already predicted, before
 * either claim had its own slot: *"an operator who ticks Optional on a protocol-required field has
 * said something the product must hear — their exchange may genuinely not need it. **If that is
 * wrong it is a validation finding**, which is the surface with room to say why."* `X46` gave the
 * two claims separate slots; this is the surface saying why when they disagree.
 *
 * **It is a warning, and it never blocks.** *Block on an invalid value, never on a missing one* —
 * and the tie-break is explicit: the operator may know something the catalogue does not, so this
 * says *this may not be what you meant* rather than refusing the configuration.
 *
 * ## What it must not become
 *
 * **A second warning about emptiness.** `validateParams` already says *"has no value and won't be
 * sent"*, and {@link describeRequiredClaim}'s own note says the required mark must not restate it.
 * So the subject here is **resolution risk**, not the empty box: a row with nothing in it at all is
 * not *derived*, and draws nothing from this.
 *
 * ## The four answers
 *
 * | The row says | |
 * | --- | --- |
 * | required + **never send** | warn — the two claims contradict, and today the badge just disappears |
 * | required + derived + **no default** | warn, naming the consequence this surface actually has |
 * | required + derived + **a default** | **nothing.** The default *prevents* the unresolved condition from firing — `wire-omission.ts` states it: *"a `sendInstead` value prevents the `unresolved` condition from ever firing, because the mapping then resolves"* |
 * | required + a **literal** | **nothing.** A static value is its own answer |
 *
 * The fourth row is why *"or has omit-if-unresolved"* is not a separate condition: `optional` only
 * bites when nothing resolves, so with a default or a literal it can never fire. What the omission
 * answer decides is the **consequence**, which is what the sentence names.
 *
 * @param fieldName the display name, **already quoted the way this surface quotes one** — the
 *   parameter rows use `"name"` and the body uses a backticked path, and a shared sentence must not
 *   overrule a surface's own convention for naming a field.
 */
export function describeRequiredRisk(
  claim: {
    required: boolean;
    suppressed: boolean;
    /** The value resolves at request time — a path, a macro or an expression — rather than a literal. */
    derived: boolean;
    /** Something is authored to send when it resolves to nothing. A blank default is not one. */
    hasDefault: boolean;
    whenUnresolved: UnresolvedBehaviour;
  },
  fieldName: string,
): string | null {
  if (!claim.required) return null;
  if (claim.suppressed) {
    return `${fieldName} is required by this destination and is set never to be sent.`;
  }
  if (!claim.derived || claim.hasDefault) return null;
  return `${fieldName} is required by this destination, but it resolves at request time and has no default — ${UNRESOLVED_CONSEQUENCE[claim.whenUnresolved]}.`;
}

/**
 * What actually happens to a required field that resolves to nothing, per surface.
 *
 * Keyed off the same {@link UnresolvedBehaviour} {@link REQUIRED_TITLE} uses, so the warning and
 * the badge's tooltip cannot come to describe one behaviour two ways — the failure
 * `sample-literal-policy.ts` was extracted to stop: *where two surfaces make the same decision,
 * they share the sentence, not just the function.*
 *
 * `sendLiteral` is a **header**, which declares no required-ness today (`declaresImportance: null`
 * — there is no catalogue of headers an exchange demands), so it is unreachable from here. It is
 * declared anyway rather than left to a fallback: the day a surface does both, the answer is
 * already written and is not whatever the last branch happened to say.
 */
const UNRESOLVED_CONSEQUENCE: Record<UnresolvedBehaviour, string> = {
  omit: 'when nothing resolves the field will be left off the request',
  sendEmpty: 'when nothing resolves it will be sent as an empty value',
  sendLiteral: 'when nothing resolves the token will be sent unchanged',
};

/** What being required actually costs, per surface. Stated, never implied. */
const REQUIRED_TITLE: Record<UnresolvedBehaviour, string> = {
  sendEmpty: 'Sent even when nothing resolves — as an empty value',
  omit: 'Expected by the receiver. Nothing resolving here leaves the field off the request',
  sendLiteral: 'Sent even when nothing resolves — as the token, unchanged',
};

/**
 * **What kind of thing each property of a `MappingLeaf` is.**
 *
 * | | |
 * | --- | --- |
 * | `identity` | which leaf this is. Never rewritten by an edit |
 * | `slot` | the answer to *what value goes here* — `N11f`: at most one may be filled, so changing tab **clears** the others |
 * | `claim` | what the leaf says about itself, independent of its value. A value edit must not disturb one |
 *
 * **This exists because the distinction was real and unwritten, so nine handlers got it wrong.**
 * `MappingControl` rebuilds the leaf from `{ path, kind, …slots }` on most changes — correct for
 * the slots and fatal for the claims — and editing the value of a required body leaf therefore
 * un-required it as you typed (Todd, 2026-08-27). The parameter surface never showed it, because
 * it merges through `applyLeafToRow` into a row it already holds; only the body hands the control
 * its stored object directly.
 *
 * **`satisfies Record<keyof MappingLeaf, …>` is the guard, and it fires in both directions**: a
 * property added to `MappingLeaf` without a kind here is a compile error, and a kind here for a
 * property that no longer exists is too. It sits in **source rather than in a spec** deliberately
 * — a console `*.test.ts` is in no referenced project, so a type-level assertion written there is
 * checked by nothing (measured 2026-08-27; see the note on {@link leafClaims}).
 */
export type LeafPropertyKind = 'identity' | 'slot' | 'claim';

export const LEAF_PROPERTY_KINDS = {
  path: 'identity',
  kind: 'identity',

  staticValue: 'slot',
  expression: 'slot',
  asType: 'slot',

  optional: 'claim',
  importance: 'claim',
  suppress: 'claim',
  provenance: 'claim',
  // **A claim, and the reshape case is the one that decides it** (`X33a`, `#1266`): *do not accept
  // the driver's value if mine does not resolve* is about the mapping, not about the value, so
  // rewriting a path as a conditional must not quietly re-enable a fall-back the operator turned
  // off. Same reasoning as `suppress` beside it.
  acceptSystemDefault: 'claim',
  // **A claim** (`#1371`): *this value is a credential* is about the row, so typing a new value into a
  // secret must not quietly make it an ordinary one.
  secret: 'claim',
  // **A claim** (`4f`, `#1416`): *how a list goes out here* is about the row, so changing the path it
  // maps must not quietly forget how the operator asked for it to be sent.
  sendAs: 'claim',
} satisfies Record<keyof MappingLeaf, LeafPropertyKind>;

/** The properties classified as claims — derived, so it cannot disagree with the table. */
export type LeafClaimKey = {
  [K in keyof typeof LEAF_PROPERTY_KINDS]: (typeof LEAF_PROPERTY_KINDS)[K] extends 'claim' ? K : never;
}[keyof typeof LEAF_PROPERTY_KINDS];

const CLAIM_KEYS = (Object.keys(LEAF_PROPERTY_KINDS) as (keyof typeof LEAF_PROPERTY_KINDS)[]).filter(
  (key): key is LeafClaimKey => LEAF_PROPERTY_KINDS[key] === 'claim',
);

/**
 * **What a leaf claims about itself, as distinct from what it maps to.**
 *
 * A change of *value* must never disturb these, and every one of `MappingControl`'s handlers goes
 * through one emitter that reapplies them — fixed there rather than at each of the nine call sites
 * that dropped them, because thirteen corrections would be right today and wrong the moment a
 * fourteenth handler is written.
 *
 * Derived from {@link LEAF_PROPERTY_KINDS} rather than listing the keys again: a claim added to
 * the leaf and not carried here would be dropped by every value edit, and that is the third time
 * in two days a claim was lost by whatever rebuilt the thing it rode on (`$optional`'s side-car,
 * then this).
 */
export function leafClaims(leaf: MappingLeaf): Pick<MappingLeaf, LeafClaimKey> {
  const claims: Partial<Pick<MappingLeaf, LeafClaimKey>> = {};
  for (const key of CLAIM_KEYS) {
    // Absent stays absent: `optional: undefined` written into the document is not the same as no
    // key, and no key is what *optional* already means everywhere.
    if (leaf[key] !== undefined) Object.assign(claims, { [key]: leaf[key] });
  }
  return claims;
}

// ---------------------------------------------------------------------------
// Path canonicalisation
// ---------------------------------------------------------------------------

/**
 * **Rewrite every context path an expression carries into its canonical spelling** (`#1213`).
 *
 * An alias is an *input spelling*: `user.deviceType` is what a person types or pastes, and
 * `session.user.deviceType` is what resolves. Todd's ruling was that what gets stored is always the
 * second, so the rewrite belongs wherever a path enters the model.
 *
 * **It is written over the model rather than over the controls, deliberately.** The Path tab, the
 * Dynamic tab's path, a computed operand and both sides of a condition are four inputs today, and
 * `XS3` collapses the tabs into one view of this same model — so a per-control fix would be
 * rewritten by the next slice while a model-level one survives it. It is also why this is not a
 * required prop on `MappingAutocompleteInput`: that control is bound to **header names** and
 * **pixel destination fields** as well as to paths, and canonicalising a destination would be
 * `X16`'s source/destination confusion.
 *
 * Recursive, because `then` and `else` are expressions and a case ladder is a right-nested chain.
 */
export function canonicalizeExpressionPaths<T extends MacroExpression | undefined>(expr: T): T {
  return mapExpressionPaths(expr, canonicalizeContextPath);
}

/**
 * **Every context path an expression reads**, in the order the walk meets them (`#1322`).
 *
 * The same walk as {@link canonicalizeExpressionPaths}, not a second one: which slots of an
 * expression hold a path is one fact, and a collector that stopped at `calculated` would miss a path
 * inside a group or on either side of a condition — `#1213`'s defect, at the depth `E3` created.
 *
 * A **reference** to a shared expression is not walked. Its paths are the library entry's, and a row
 * addressing it does not hold them.
 */
export function expressionPaths(expr: MacroExpression | undefined): string[] {
  const paths: string[] = [];
  mapExpressionPaths(expr, (path) => {
    if (path) paths.push(path);
    return path;
  });
  return paths;
}

/** Rewrite every context path an expression carries through `rewrite` — the one walk over its path slots. */
export function mapExpressionPaths<T extends MacroExpression | undefined>(
  expr: T,
  rewrite: (path: string) => string,
): T {
  if (!expr) return expr;

  switch (expr.type) {
    case 'calculated':
      return { ...expr, path: rewrite(expr.path) } as T;
    case 'computed':
      return {
        ...expr,
        /**
         * **An operand is an expression since `E3`, so it canonicalises through this same function**
         * — which is what carries a **group**'s operands down with it. Handling `calculated` here
         * and stopping would have left a path inside a group uncanonicalised, silently, which is
         * `#1213`'s defect at exactly the depth `E3` created.
         *
         * The pre-`E3` `path` operand keeps its own line: it is read and never written, so it must
         * still canonicalise on a document nobody has re-saved.
         */
        operands: expr.operands.map((o) =>
          // A collection's list is a context path and canonicalises like one; its member is relative
          // to each member, which no alias names (`CO8`, `2e`).
          o.type === 'path' || o.type === 'collection'
            ? { ...o, path: rewrite(o.path) }
            : o.type === 'literal' || o.type === 'fn'
              ? o
              : mapExpressionPaths(o, rewrite),
        ),
      } as T;
    case 'conditional':
      return {
        ...expr,
        condition: mapCriteriaPaths(expr.condition, rewrite),
        then: mapExpressionPaths(expr.then, rewrite),
        ...(expr.else ? { else: mapExpressionPaths(expr.else, rewrite) } : {}),
      } as T;
    default:
      // `static` and `suppress` carry no path. A static value is the author's and is never touched.
      return expr;
  }
}

/** Both sides of every comparison in a criteria tree — the value path and the right-hand path. */
function mapCriteriaPaths(node: CriteriaNode, rewrite: (path: string) => string): CriteriaNode {
  switch (node.type) {
    case 'condition':
      return {
        type: 'condition',
        condition: {
          ...node.condition,
          ...(node.condition.path !== undefined ? { path: rewrite(node.condition.path) } : {}),
          ...(node.condition.valuePath !== undefined ? { valuePath: rewrite(node.condition.valuePath) } : {}),
        },
      };
    case 'all':
    case 'any':
      return { type: node.type, of: node.of.map((n) => mapCriteriaPaths(n, rewrite)) };
    case 'not':
      return { type: 'not', of: mapCriteriaPaths(node.of, rewrite) };
    default:
      return node;
  }
}

// ---------------------------------------------------------------------------
// Artifact metadata on a wire surface
// ---------------------------------------------------------------------------

/**
 * **A mapping that sends metadata about the artifact rather than content of it** (`#1322`).
 *
 * Todd, 2026-09-14: until `when` exists, artifact metadata *"stays offered with a warning, no
 * blocking"*; the end state excludes it by default on wire writes and allows it on custom pixels
 * (`#1308`). This is the interim half, so it is a **warning and never a block**: the mapping is legal
 * and the driver sends it.
 *
 * **One sentence for every wire surface**, because the three make the same decision and a duplicated
 * explanation drifts where nothing checks it. Pixels do not call it — a pixel is not the request a
 * partner expects, which is the split `#1308` makes permanent.
 *
 * Reads every path the value holds — the row's own path and each path inside its expression — and
 * reports each classified field once. Returns one sentence per field.
 */
export function describeArtifactMetadataOnWire(
  value: { mappingSource?: string; expression?: MacroExpression },
  /** The row or leaf, already formatted as the surface names it — `"pubid"` or `` `ext.creator` ``. */
  subject: string,
): string[] {
  const seen = new Set<string>();
  const messages: string[] = [];
  for (const raw of [value.mappingSource ?? '', ...expressionPaths(value.expression)]) {
    if (!raw) continue;
    const at = artifactFieldAt(raw);
    if (!at || seen.has(at.path)) continue;
    seen.add(at.path);
    const user = at.declaration.namesAUser ? 'names a user of this system and ' : '';
    messages.push(
      `${subject} is mapped to ${at.path}, which ${user}is metadata about the ${at.artifact} rather than part of the request a partner expects. It will be sent as mapped.`,
    );
  }
  return messages;
}
